import React, { useState } from 'react';
import { usePackages } from '@/hooks/usePackages';
import { usePlans } from '@/hooks/usePlans';
import { useChannels } from '@/hooks/useChannels';
import { useCategories } from '@/hooks/useCategories';
import { usePackageById } from '@/hooks/usePackages';
import { packageMutations } from '@/services/packages/mutations';
import { PackageTable } from '../components/PackageTable';
import { PackageComposer } from '../components/PackageComposer';
import { PackageCard } from '../components/PackageCard';
import { PackageFormData, CreatePackageParams, UpdatePackageParams } from '@/features/packages/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, LayoutGrid, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import type { VersionAwareProps } from '@/features/versions/types';

export const PackagesPage: React.FC<VersionAwareProps> = (props) => {
  const { providerId, versionId, isReadOnly } = props;
  const regionId = (props as any).regionId ?? 0;

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<number | null>(null);

  // Fetch data
  const { packages, isLoading: packagesLoading, createPackage, updatePackage } = usePackages(
    providerId,
    versionId,
    regionId
  );

  const { data: plans = [] } = usePlans(providerId, versionId);
  const { data: channels = [] } = useChannels(providerId, versionId);
  const { data: categories = [] } = useCategories(providerId, versionId);
  const { package: editingPackage } = usePackageById(editingPackageId, versionId);

  const handleCreate = async (
    packageData: PackageFormData,
    planIds: number[],
    categoryIds: number[],
    channelIds: number[]
  ) => {
    try {
      const params: CreatePackageParams = {
        ...packageData,
        provider_id: providerId,
        version_id: versionId,
        region_id: regionId,
        plan_ids: planIds,
        category_ids: categoryIds,
        channel_ids: channelIds,
      };

      await packageMutations.createPackage(params);
      toast.success('Package created successfully');
      setIsComposerOpen(false);
    } catch (error) {
      toast.error('Failed to create package');
    }
  };

  const handleUpdate = async (
    packageData: PackageFormData,
    planIds: number[],
    categoryIds: number[],
    channelIds: number[]
  ) => {
    if (!editingPackageId) return;

    try {
      const params: UpdatePackageParams = {
        id: editingPackageId,
        data: packageData,
        plan_ids: planIds,
        category_ids: categoryIds,
        channel_ids: channelIds,
      };

      await packageMutations.updatePackage(params);
      toast.success('Package updated successfully');
      setEditingPackageId(null);
      setIsComposerOpen(false);
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  const handleDeletePackage = async (packageId: number) => {
    try {
      await packageMutations.deletePackage(packageId);
      toast.success('Package deleted successfully');
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Packages</h1>
          <p className="mt-1 text-gray-600">
            Manage packages for your provider
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingPackageId(null);
            setIsComposerOpen(true);
          }}
          size="lg"
          disabled={isReadOnly}
        >
          <Plus className="mr-2 h-4 w-4" />
          New Package
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Packages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{packages.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Featured</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {packages.filter((p) => p.is_featured).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Add-ons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {packages.filter((p) => p.is_addon).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Channels</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {packages.length > 0
                ? Math.round(
                    packages.reduce((sum, p) => sum + (p.channel_count || 0), 0) / packages.length
                  )
                : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Controls */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Packages List</h2>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'list' ? (
        <PackageTable
          packages={packages}
          isLoading={packagesLoading}
          onEdit={(pkg) => {
            setEditingPackageId(pkg.id);
            setIsComposerOpen(true);
          }}
          onDelete={handleDeletePackage}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              package={pkg}
              onEdit={(p) => {
                setEditingPackageId(p.id);
                setIsComposerOpen(true);
              }}
              onDelete={handleDeletePackage}
            />
          ))}
        </div>
      )}

      {/* Composer Dialog */}
      <Dialog open={isComposerOpen} onOpenChange={setIsComposerOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPackageId ? 'Edit Package' : 'Create New Package'}
            </DialogTitle>
            <DialogDescription>
              {editingPackageId
                ? 'Update package details and composition'
                : 'Create a new package by configuring details and selecting plans, categories, and channels'}
            </DialogDescription>
          </DialogHeader>

          {editingPackageId && editingPackage ? (
            <PackageComposer
              initialData={editingPackage}
              availablePlans={plans}
              availableCategories={categories}
              availableChannels={channels}
              isSubmitting={updatePackage.isPending}
              onSubmit={handleUpdate}
              onCancel={() => setIsComposerOpen(false)}
            />
          ) : (
            <PackageComposer
              availablePlans={plans}
              availableCategories={categories}
              availableChannels={channels}
              isSubmitting={createPackage.isPending}
              onSubmit={handleCreate}
              onCancel={() => setIsComposerOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
